/**
 * Peer Service
 * Unified peer interactions: discovery, CRUD, config, messaging
 */

import { mergePeers } from './peer.js'
import { createChatEngine } from './chat-engine.js'

export function createPeerService({
  botConfigService, discoverRoles, roleCall, deleteRole, excludeBotIds,
  // Optional chat engine deps — if provided, sendMessage delegates to chat engine
  chatFn, executeTool, buildPrompt, conversationToPrompt, hostId,
  // Optional participant storage — if provided, sendMessage auto-resolves participants
  getParticipants, setParticipants,
}) {
  let cachedPeers = []
  let cachedBots = []

  const discover = async (opts) => {
    const [bots, roles] = await Promise.all([
      botConfigService.getBots(),
      discoverRoles(opts),
    ])
    cachedBots = bots
    cachedPeers = mergePeers(bots, roles, excludeBotIds)
    return { peers: cachedPeers, bots, roles }
  }

  const getPeer = (roleId) => cachedPeers.find(p => p.roleId === roleId) || null

  const getBot = (botId) => cachedBots.find(b => b.botId === botId) || null

  const addBot = (config) => botConfigService.createBot(config)

  const updateBot = (botId, updates) => botConfigService.updateBot(botId, updates)

  const removePeer = async (roleId) => {
    const peer = getPeer(roleId)
    if (peer?.type === 'llm-bot') {
      await botConfigService.deleteBot(roleId)
    } else {
      await deleteRole(roleId)
    }
  }

  const getWorkerConfig = (roleId) => roleCall(roleId, 'getConfig', [])

  const updateWorkerConfig = (roleId, values) => roleCall(roleId, 'updateConfig', [values])

  const chatEngine = chatFn
    ? createChatEngine({ chatFn, executeTool, buildPrompt, conversationToPrompt, roleCall, hostId })
    : null

  const resolveParticipants = async () => {
    if (!getParticipants) return { participants: [], discovered: false }
    const stored = await getParticipants()
    if (stored.length > 0) return { participants: stored, discovered: false }
    const { roles } = await discover()
    const online = (roles || []).filter(r => r.online)
    if (online.length > 0 && setParticipants) await setParticipants(online)
    return { participants: online, discovered: online.length > 0 }
  }

  const sendMessage = async (content, options = {}) => {
    if (!chatEngine) throw new Error('Chat engine not configured')

    // Auto-resolve participants for main chat (no explicit target)
    let participants = options.participants
    let discovered = false
    if (!options.target && participants === undefined) {
      const resolved = await resolveParticipants()
      participants = resolved.participants
      discovered = resolved.discovered
    }

    // Enrich onAsyncTask with displayName from resolved participants
    const { onAsyncTask, ...rest } = options
    const wrappedOnAsyncTask = onAsyncTask
      ? (info) => {
          const p = participants?.find(pp => pp.roleId === info.roleId)
          onAsyncTask({ ...info, displayName: info.displayName || p?.displayName || info.roleId?.slice(0, 8) || 'Remote' })
        }
      : undefined

    const result = await chatEngine.sendMessage(content, { ...rest, participants, onAsyncTask: wrappedOnAsyncTask })
    if (discovered) result.participants = participants
    return result
  }

  return { discover, getPeer, getBot, addBot, updateBot, removePeer, getWorkerConfig, updateWorkerConfig, sendMessage }
}
